import HBList from "@/components/HBList";
import Image from "@/components/Image";
import { useLogin } from "@/components/LoginModal";
import useAppStore from "@/context/store";
import { getBoardPins } from "@/services";
import { getContentType } from "@/utils/getContentType";
import imageUrl from "@/utils/imageUrl";
import useRequest from "@/utils/useReq";
import { useSetState } from "ahooks";
import classNames from "classnames";
import { last } from "lodash";
import { useCallback, useEffect, useState } from "react";
import styles from "./index.module.less";

const masonryOptions = {
  threshold: 200,
  gutter: 2,
  columnSize: 82.5,
  brickId: "pin_id",
};

const scrollableTarget = "pinBoardPinList";

const infiniteScrollOptions = {
  endMessage: null,
  loader: null,
};

export default function PinBoardPinList({ pin, onSelect }) {
  const user = useAppStore((s) => s.user);
  const { login } = useLogin();
  const pinId = pin?.pin_id;
  const boardId = pin?.board_id;
  const [query, setQuery] = useSetState({
    max: last(pin?.board?.pins)?.pin_id || 0,
    boardId: boardId,
  });
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState(pin?.board?.pins || []);
  useRequest(
    async () => {
      const { max, boardId } = query;

      if (!boardId) {
        return;
      }

      const result = await getBoardPins({
        board_id: boardId,
        limit: 20,
        ...(max ? { max } : null),
      });
      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource(dataSource.concat(pins));
    },
    {
      refreshDeps: [query],
    }
  );

  useEffect(() => {
    const pins = pin?.board?.pins || [];
    if (boardId) {
      setHasMore(true);
      setDataSource(pins);
      const last = pins[pins.length - 1];
      if (last) {
        setQuery({
          boardId,
          max: last.seq || last.pin_id,
        });
      }
    }
  }, [boardId, pin?.board?.pins, setQuery]);

  const onLoadMore = useCallback(() => {
    const pins = pin?.board?.pins || [];
    if (dataSource.length > pins.length) {
      const last = dataSource[dataSource.length - 1];
      setQuery({
        max: last.seq || last.pin_id,
      });
    }
  }, [dataSource, pin, setQuery]);

  const render = useCallback(
    (pin) => {
      const height = Math.min(
        260,
        Math.round((pin.file?.height / pin.file?.width) * 82.5)
      );

      const onClick = async (evt) => {
        if (evt.metaKey || evt.ctrlKey) {
          return;
        }

        evt.preventDefault();
        if (!user) {
          login();
        }

        onSelect(pin, imageUrl(pin.file, { width: 82.5 }), "sameBoard");
      };

      const onImgLoad = (evt) => {
        const target = evt.target;
        target?.classList.add("transparent-img-bg");
      };

      return (
        <a
          className={classNames([
            styles.item,
            pinId === pin?.pin_id ? styles.active : null,
          ])}
          style={{ height }}
          key={pin.pin_id}
          href={`/pins/${pin.pin_id}`}
          onClick={onClick}
          data-gd-expose="content_expose"
          data-content-type={getContentType(pin?.extra)}
          data-gd-click="material_click"
          data-content-id={pin.pin_id}
          data-material-id={pin.extra?.material_id}
          data-board-id={boardId}
          data-file-id={pin.file_id}
          data-ext={JSON.stringify(pin?.ext)}
        >
          <div className={styles.img}>
            <Image
              onLoad={onImgLoad}
              src={imageUrl(pin.file, { width: 82.5 })}
              width={82.5}
              height={Math.round((pin.file?.height / pin.file?.width) * 82.5)}
              alt={pin.raw_text}
              showError={true}
            />
          </div>
        </a>
      );
    },
    [pinId, boardId, user, onSelect, login]
  );

  return (
    <div
      className={styles.preview}
      id={scrollableTarget}
      data-content-source="同画板"
    >
      <HBList
        dataSource={dataSource}
        uniqueKey={"pin_id"}
        scrollableTarget={scrollableTarget}
        render={render}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        masonryOptions={masonryOptions}
        infiniteScrollOptions={infiniteScrollOptions}
      />
    </div>
  );
}
